import { DateTime } from "luxon";
import { addToRange, defaultDateLib, type DateRange } from "react-day-picker";
import type { DateBounds } from "../types";
import { isWithinBounds } from "./isWithinBounds";
import { maxRangeSlidingWindow } from "./maxRangeSlidingWindow";
import { normalizeDateRange } from "./normalizeDateRange";
import { startOfLocalDay } from "./startOfLocalDay";

function startRangeFrom(day: Date): DateRange {
  return { from: day, to: undefined };
}

function rangeLongerThanMaxDays(
  ordered: { from: Date; to: Date },
  maxRangeDays: number | undefined,
): boolean {
  if (!maxRangeDays || !Number.isFinite(maxRangeDays) || maxRangeDays <= 0) {
    return false;
  }
  const latestAllowedEnd = DateTime.fromJSDate(startOfLocalDay(ordered.from))
    .plus({ days: maxRangeDays - 1 })
    .toJSDate();
  return ordered.to.getTime() > latestAllowedEnd.getTime();
}

export function reduceRangeAfterDayClick(
  prev: DateRange | undefined,
  clicked: Date,
  allowableBounds: DateBounds,
  maxRangeDays: number | undefined,
): DateRange | undefined {
  const day = startOfLocalDay(clicked);

  // Not in the dates we allow -> ignore.
  if (!isWithinBounds(day, allowableBounds)) return prev;

  const bounds = maxRangeSlidingWindow(prev, maxRangeDays);
  if (
    bounds.min != null &&
    bounds.max != null &&
    !isWithinBounds(day, bounds)
  ) {
    // Too far from current selection -> new range from this day.
    return startRangeFrom(day);
  }

  // First click: start only.
  if (!prev?.from && !prev?.to) {
    return startRangeFrom(day);
  }

  // Further clicks: grow, shrink, or finish the range.
  const next = addToRange(day, prev, 0, 0, false, defaultDateLib);

  if (next == null) {
    return undefined;
  }

  const from = next.from != null ? startOfLocalDay(next.from) : undefined;
  const to = next.to != null ? startOfLocalDay(next.to) : undefined;

  if (from && to) {
    const ordered = normalizeDateRange(from, to);
    if (rangeLongerThanMaxDays(ordered, maxRangeDays)) {
      // Span too long -> start again from this day.
      return startRangeFrom(day);
    }
    if (
      !isWithinBounds(ordered.from, allowableBounds) ||
      !isWithinBounds(ordered.to, allowableBounds)
    ) {
      // Range would leave allowed dates → start again from this day.
      return startRangeFrom(day);
    }
    return ordered;
  }

  if (from && !isWithinBounds(from, allowableBounds)) {
    // Proposed start not allowed → leave selection unchanged.
    return prev;
  }

  return from ? { from, to: undefined } : prev;
}
